import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { usePersonaThumbFallback } from '@/components/shared/PersonaThumbImg'
import { SubsegmentChip, SubsegmentOverflowChip } from '@/components/twin-chat'
import { useState, useMemo, useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { SingleChoiceChart, NumericChart, MatrixChart, TextResponsesList, RankingChart, ChartJsGroupedBar } from '@digital-twins/deck-ui/components'
import {
  laneAssetIdsForQuestion,
  laneMergedChoiceShares,
  isLaneMergedQuestion,
  declaresPerAssetBinding,
} from '@digital-twins/deck-ui/lib/monadicLaneScope'
import type { LaneMergedChoiceShares, MonadicLaneScopeFact } from '@digital-twins/deck-ui/lib/monadicLaneScope'
import { RadarChart } from '@digital-twins/deck-ui/components/charts/RadarChart/RadarChart'
import type { RadarSeries } from '@digital-twins/deck-ui/components/charts/RadarChart/RadarChart'
import { aggregateSimple, aggregateMatrixDropdown, isMatrixDropdown, getChartableMatrixColumns, getNumericMatrixColumns, getQuestionValue, isPlainObjectAnswer } from '@/lib/utils/aggregation'
import { respondentAnswered } from '@digital-twins/deck-ui/lib/aggregation'
import { formatLabel } from '@/lib/utils/formatting'
import { rankingChartLabelsForLanguage } from '@/lib/charts/rankingChartLabels'
import { getAppChartPalette } from '@/lib/charts/chartjs'
import { previewsForAssets, withVersionTitles } from '@/features/studies/report/utils/testedAssets'
import { LaneMediaBars } from './LaneMediaBars'
import { useContentLanguage } from '@/i18n/contentLanguageContext'
import { intlLocaleForUi } from '@/i18n'
import { useTranslation } from 'react-i18next'
import { useTextResponsesListLabels } from '../../deck/deckChromeLabels'
import { useBrandOptional } from '@/lib/brand-provider'
import { CHART_LIMITS } from '@digital-twins/deck-ui/foundations/slide-limits'
import { declaredDemographicDimensionId } from '@digital-twins/shared-types'
import { SegmentationMapping } from '@/types/components/audiences/segmentation-mapping'
import { formatAssetReportLabel, disambiguatedAssetReportLabels } from '@digital-twins/deck-ui/components/charts/asset-report-label'
import {
  collectIncidenceVariants,
  formatIncidenceCell,
} from '@digital-twins/deck-ui/slides/QualThemeIncidenceGrid'
import {
  liftQuestionIdsOf,
  compiledGridRowCount,
  readLiftScorecardForPair,
  readLiftTagsFromQuestion,
  resolveLiftPair,
} from '@/features/studies/questionnaire/assets/liftTags'
import { LiftHalfChip } from '@/features/studies/questionnaire/components/LiftHalfChip'
import { LiftView } from '../../tabs/LiftView'
import { buildBlurbLine, inferAudienceType } from '@/lib/utils/twin-blurb'
import { reportQuestionKindLabel, resolveReportQuestionKind } from './questionTypeLabel'
import { segmentThemeShares } from './segmentThemeShares'
import type { SegmentThemeShare } from './segmentThemeShares'
import { useQuestionCardFigures } from '@/features/studies/report/figures/useQuestionCardFigures'
import { FigureEntryStatus, QuestionFigureView } from '@/features/studies/report/figures/QuestionFigureView'
import { ThemeCardFigure } from '@/features/studies/report/figures/views/ThemeCardFigure/ThemeCardFigure'
import { OpenEndQuoteGallery } from './OpenEndQuoteGallery'
import { FigureSegmentControls } from '@/features/studies/report/figures/FigureSegmentControls'
import {
  AssetQuestionBinding,
  CanonicalAnalysisOutput,
  QualitativeQuote,
  QualitativeThemePackage,
  QualitativeThemeVariantShare,
  StudyFigureThemeCards,
  TestedAsset,
  isRatingUnitId,
  respondentWordingFor,
} from '@digital-twins/shared-types'

/**
 * Name / role / portrait for a respondent quoted by the qualitative analysis.
 * Emitted server-side by `build_quote_respondent_profiles`
 * (`canonical_deck/respondent_media.py`) onto `summaryJson.quoteRespondentProfiles`,
 * covering every quoted respondent rather than only the featured-quote subset
 * the Summary tab reads. Joined here by `respondentId` / `respondentLabel`.
 */
export interface QuoteRespondentProfile {
  respondentId?: string
  respondentLabel?: string
  name?: string
  /** The speaker's label (`canonical_analysis/speakers.py`, D16). */
  role?: string
  /**
   * What `role` rests on: `study` (the person's own record and answers) or
   * `demographic`. Absent on a profile stored before the speaker owner; its
   * `role` may be a persona tagline and is never shown.
   */
  labelBasis?: 'study' | 'demographic'
  imageUrl?: string
}

interface QuestionCardProps {
  question: any
  /**
   * The other half of a `lift:pre:<measure>` / `lift:post:<measure>` pair.
   * When present the card toggles which question is aggregated — both names
   * already live on the same `responses` rows.
   */
  pairedQuestion?: any
  questionNumber?: number
  segmentableQuestions: any[]
  responses: any[]
  populationAttributes?: string[]
  canonicalAnalysis?: CanonicalAnalysisOutput | null
  canonicalQuestionMap?: Record<string, CanonicalQuestionMapEntry>
  assetContextItems?: Array<{ assetId: string; title?: string; [key: string]: unknown }>
  /** Tested creatives, so a lane chart can swap a written name for a preview. */
  testedAssets?: Array<TestedAsset>
  quoteRespondentProfiles?: Array<QuoteRespondentProfile>
  contentLanguage?: string
  /**
   * `stacked` forces a single column regardless of the xl viewport breakpoint.
   * Used by the summary plot-slot fallback, whose column is well under 1280px.
   */
  layout?: 'default' | 'stacked'
  /**
   * The audience's segmentation mappings. A population attribute whose mapping
   * names a declared canonical variable (`std_dem_age`) is that demographic,
   * whatever its column is called.
   */
  segmentationMappings?: Array<SegmentationMapping> | null
  /**
   * The figure service's id for a card that is not a canonical question: a
   * population column (`populationFigureId`, Wave 7 S5).
   */
  figureQuestionId?: string | null
}

/**
 * The segment question for one population attribute column. Its declared
 * demographic comes from the column's mapping `canonicalId` first, then an
 * exact declared key, so a mapped `respondent_age` bins like `age`.
 */
export function buildPopulationAttributeQuestion(
  name: string,
  segmentationMappings?: Array<SegmentationMapping> | null,
) {
  const mapping = (segmentationMappings || []).find((item) => item.sourceColumn === name)
  return {
    name,
    title: name,
    type: 'population_attribute',
    isPopulationAttribute: true,
    demographicAttribute: declaredDemographicDimensionId({
      key: name,
      canonicalId: mapping?.canonicalId ?? null,
    }),
  }
}

/**
 * Row keys a true-monadic run may carry the assigned cut under. survey-engine
 * stamps `assigned_asset_id`; the rest are accepted spellings so a re-serialized
 * results file still resolves. Mirrors the python reader in
 * `canonical_analysis/runtime.py` (`_CELL_ID_ROW_KEYS`).
 */
const ASSET_SEGMENT_ROW_KEYS: Array<string> = [
  'assigned_asset_id',
  'assignedAssetId',
  'assigned_cut_id',
  'assignedCutId',
]

const ASSET_SEGMENT_NAME = 'assigned_asset_id'
const ASSET_SEGMENT_TITLE = 'Asset shown'

/** Stamped theme cards shown before "Show N more themes". */
const THEME_CARDS_VISIBLE = 6

/**
 * Question types whose chart prints its own respondent base at the bottom
 * (NumericChart's "Based on N responses", MatrixChart's per-row/column n).
 * Read by `chartPrintsOwnBase`, `stacked` layout only.
 */
const CHART_TYPES_WITH_OWN_BASE_FOOTER: Set<string> = new Set([
  'rating',
  'matrix',
  'matrixdropdown',
  'matrixdynamic',
])

/**
 * A compiled rating with a fully labeled scale can be a SurveyJS radiogroup
 * (notably when it carries an N/A option). The canonical unit preserves its
 * real family, so presentation never guesses from option wording.
 */
function isStackedAssetScale(question: any): boolean {
  const unit = question?._canonical?.unit
  return (
    typeof unit === 'string' &&
    isRatingUnitId(unit) &&
    unit !== 'binary_yes_no' &&
    unit !== 'rating_0_100'
  )
}

/**
 * One 100% stacked row per tested item. Typed rating scales already
 * transpose this way; a lane-merged single-select is the same composition
 * (shares sum to 100 per item). Multi-select stays grouped because a
 * respondent can pick several options.
 */
/** The shared per-lane base, or null when the lanes were not asked of the same people. */
function uniformPositiveBase(series: Array<{ base: number }> | undefined): number | null {
  const answered = (series ?? []).filter((entry) => entry.base > 0)
  if (answered.length === 0) {
    return null
  }
  const first = answered[0].base
  if (!answered.every((entry) => entry.base === first)) {
    return null
  }
  return first
}

function shouldStackLaneMergedChoice(question: any): boolean {
  if (isStackedAssetScale(question)) {
    return true
  }
  return (
    question?.type === 'radiogroup' ||
    question?.type === 'dropdown' ||
    question?.type === 'boolean'
  )
}

/**
 * Question types whose charts can actually split by a segment. matrixdropdown /
 * matrixdynamic are excluded: MatrixChart has no segmented path for them and
 * shows "Segmentation is not available yet" if a segment is applied.
 */
const SEGMENTABLE_QUESTION_TYPES: Array<string> = [
  'radiogroup',
  'dropdown',
  'boolean',
  'checkbox',
  'rating',
  'matrix',
  'ranking',
]

export function questionSupportsSegmentation(question: any): boolean {
  return SEGMENTABLE_QUESTION_TYPES.includes(question?.type)
}

function responsesMatchingSegmentValue(
  responses: Array<any>,
  segmentQuestion: any | undefined,
  segmentValue: string | null,
): Array<any> {
  if (!segmentQuestion || segmentValue == null) return responses
  const target = String(segmentValue)
  return responses.filter((response) => {
    const value = getQuestionValue(response, segmentQuestion)
    if (value === undefined || value === null || isPlainObjectAnswer(value)) return false
    if (Array.isArray(value)) {
      return value.some((item) => String(item) === target)
    }
    return String(value) === target
  })
}

/**
 * Synthetic segment question for the cut a respondent was assigned, or null when
 * no row carries one. Fails closed: a comparative (non-monadic) study has no such
 * key on any row, so the Assets group never renders there.
 */
/**
 * Readable label for a raw asset id when no authored title is available.
 *
 * Asset ids are generated slugs (`asset_ad_2`, `asset_landing_page_1`), which are
 * fine as data keys and unacceptable as chart labels. Strips the `asset_` prefix,
 * un-snake-cases the rest, and title-cases it: `asset_ad_2` -> "Ad 2". Generic over
 * any asset family, never keyed to a specific study or id.
 */
export function humanizeAssetId(value: string): string {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return trimmed

  const words = trimmed
    .replace(/^asset[_-]/i, '')
    .split(/[_-]+/)
    .filter((word) => word.length > 0)

  if (words.length === 0) return trimmed

  return words
    .map((word) => (/^\d+$/.test(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ')
}

export function buildAssetSegmentQuestion(
  responses: Array<any>,
  assetContextItems?: Array<{ assetId: string; title?: string; [key: string]: unknown }>,
): any | null {
  const probe = {
    name: ASSET_SEGMENT_NAME,
    title: ASSET_SEGMENT_TITLE,
    type: 'population_attribute',
    isPopulationAttribute: true,
    attributeAliases: ASSET_SEGMENT_ROW_KEYS,
  }

  const rawValues = new Set<string>()
  for (const response of responses || []) {
    const raw = getQuestionValue(response, probe)
    if (typeof raw === 'string' && raw.trim().length > 0) {
      rawValues.add(raw)
    }
  }

  if (rawValues.size === 0) return null

  const titleByAssetId = new Map<string, string>()
  for (const item of assetContextItems || []) {
    const assetId = String(item?.assetId ?? '').trim()
    const title = typeof item?.title === 'string' ? item.title.trim() : ''
    if (assetId && title) {
      titleByAssetId.set(assetId, title)
    }
  }

  const choices = Array.from(rawValues).map((value) => {
    const fallback = humanizeAssetId(value)
    const titled = titleByAssetId.get(value.trim())
    return {
      value,
      text: formatAssetReportLabel(titled || fallback, assetContextItems, [value]),
    }
  })

  return { ...probe, choices }
}

function isRadarCandidate(question: any): boolean {
  if (!isMatrixDropdown(question)) return false
  if (!question.columns || !question.rows) return false
  if (question.rows.length < 2) return false

  return getNumericMatrixColumns(question).length >= 3
}

function buildRadarData(
  question: any,
  responses: any[],
  assetContextItems?: Array<{ assetId: string; title?: string; [key: string]: unknown }>,
): { labels: string[]; series: RadarSeries[]; min: number; max: number } | null {
  if (!question.columns || !question.rows) return null

  const numericColumns = getNumericMatrixColumns(question)
  if (numericColumns.length < 3) return null

  const columnDefs = numericColumns.map((c: any) => ({
    value: String(c.name || c.value || c),
    text: c.text || c.title || c.name || c.value || c,
  }))

  const labels = columnDefs.map((c: any) => formatLabel(String(c.text)))

  let scaleMin = 1
  let scaleMax = 5
  const firstCol = numericColumns[0]
  const cellType = String(firstCol?.cellType || question.cellType || '').toLowerCase()
  if (cellType === 'rating') {
    scaleMin = Number(firstCol?.rateMin ?? question.rateMin ?? 1)
    scaleMax = Number(firstCol?.rateMax ?? question.rateMax ?? 5)
    if (!Number.isFinite(scaleMin)) scaleMin = 1
    if (!Number.isFinite(scaleMax)) scaleMax = 5
  } else {
    const allChoiceValues = numericColumns.flatMap((c: any) =>
      (c?.choices || question.choices || [])
        .map((ch: any) => Number(ch?.value ?? ch))
        .filter((v: number) => !isNaN(v) && Number.isFinite(v))
    )
    if (allChoiceValues.length > 0) {
      scaleMin = Math.min(...allChoiceValues)
      scaleMax = Math.max(...allChoiceValues)
    }
  }

  try {
    const result = aggregateMatrixDropdown(question, responses)
    const seriesData = result.data.slice(0, 8)
    const series: RadarSeries[] = seriesData.map((row) => ({
      label: formatLabel(formatAssetReportLabel(row.rowName, assetContextItems)),
      data: columnDefs.map((colDef: any) => {
        const col = row.columns.find((c) => c.value === colDef.value)
        return col?.avgRating != null ? Math.round(col.avgRating * 10) / 10 : 0
      }),
      fill: true,
    }))
    return series.length > 0 ? { labels, series, min: scaleMin, max: scaleMax } : null
  } catch {
    return null
  }
}

interface CanonicalQuestionMapEntry {
  questionId?: string
  stableKey?: string
  qualitativeAnalysisIds?: string[]
  /**
   * Compiled passthrough of the draft question's `tags`
   * (`apps/backend/src/questionnaires/compiler/service.ts`). Carries the
   * `lift:pre:<measure>` / `lift:post:<measure>` half, and is read here as the
   * fallback for a survey whose element lost its own `x_dt_tags`.
   */
  x_dt_tags?: Array<string>
  /**
   * Mirror of the element's compiled `assetBinding`. Read as the fallback
   * when a re-serialized survey element lost its `_canonical` block.
   */
  assetBinding?: Partial<AssetQuestionBinding>
  /** Compiled SurveyJS element name and canonical type of the entry. */
  surveyJsName?: string
  canonicalType?: string
  metricIds?: Array<string>
  slideIds?: Array<string>
  /** Declared price role of a price question, read by the numeric chart. */
  priceRole?: string | null
}

/** A theme quote that keeps its speaker identity, so the card can attribute it. */
export interface ThemeQuoteRef {
  text: string
  respondentId?: string
  respondentLabel?: string
  /** The asset lane the quote was given in, on a `per_asset` question. */
  assetId?: string
}

interface ThemeSummaryItem {
  /** The package's theme id: the figure service's stamped theme cards join their words by it. */
  id: string
  theme: string
  description: string
  citation_indices: number[]
  percent?: number
  /**
   * Quote text only. Kept as `string[]` because `themeSummary` is handed to
   * deck-ui's `TextResponsesList`, whose `TextSummary` contract types it that
   * way. `quoteRefs` carries the same quotes with their speaker identity.
   */
  quotes?: string[]
  quoteRefs?: Array<ThemeQuoteRef>
  /** Per-asset incidence from a shared codeframe; absent on pooled extraction. */
  variantShares?: Array<ThemeVariantShareRef>
}

/**
 * One theme's per-variant incidence as the card and deck-ui's incidence grid
 * read it. The stored share (generated analysis shape) may carry
 * `answerBase: null`, which means no base was recorded: it is left out here.
 */
interface ThemeVariantShareRef {
  variantId: string
  label?: string
  frequency: number
  percent: number
  answerBase?: number
}

function toVariantShareRef(share: QualitativeThemeVariantShare): ThemeVariantShareRef {
  const { answerBase, ...rest } = share
  return answerBase == null ? rest : { ...rest, answerBase }
}

interface ThemeSummary {
  themes: ThemeSummaryItem[]
}

export interface PerAssetScope {
  /** Human noun for one lane, from the binding's asset type: "concept", "landing page". */
  noun: string
  /** One label per lane, resolved from the asset context the charts label with. */
  labels: Array<string>
}

/**
 * HISTORICAL READER: only for an analysis
 * written before `monadicLaneScope` existed; `resolveCardLaneScope` picks it.
 *
 * The lanes a `per_asset` question was asked in, or null for any other
 * binding. The compiler stamps the binding on the element (`_canonical`) and
 * mirrors it on the questionMap entry; either is accepted. The lanes come from
 * the same asset context the charts label with, narrowed to the binding's
 * asset types when both sides declare one. Nothing is inferred from the title.
 */
export function readPerAssetScope(
  question: any,
  canonicalQuestionMap?: Record<string, CanonicalQuestionMapEntry>,
  assetContextItems?: Array<{ assetId: string; title?: string; [key: string]: unknown }>,
): PerAssetScope | null {
  const binding: Partial<AssetQuestionBinding> | undefined =
    question?._canonical?.assetBinding ?? canonicalQuestionMap?.[question?.name]?.assetBinding
  if (binding?.mode !== 'per_asset') return null

  const boundTypes = (binding.assetTypes || []).map((type) => String(type))
  const lanes = (assetContextItems || []).filter((item) => {
    // A catalog item (a pain, a brand) is named in questions and never shown,
    // so it is never a lane: the engine's `is_catalog_asset_item` rule.
    if (item.role === 'catalog') {
      return false
    }
    const itemType = typeof item.assetType === 'string' ? item.assetType : ''
    return boundTypes.length === 0 || !itemType || boundTypes.includes(itemType)
  })
  if (lanes.length === 0) return null

  return {
    noun: boundTypes.length === 1 ? boundTypes[0].replace(/_/g, ' ') : 'asset',
    labels: lanes.map((item) => formatAssetReportLabel(item.assetId, assetContextItems)),
  }
}

/**
 * The lanes a TWIN-MONADIC question was asked in, from the study-level fact.
 *
 * Sibling of `readPerAssetScope`, which only ever fires for a `per_asset`
 * binding — so a twin-monadic question (bound `all_assets`, asset-keyed by the
 * engine anyway) showed its lane-keyed answer dict with NO scope line at all.
 *
 * Scope comes from `monadicLaneScope` and nothing else: never from
 * `assetBinding.mode`, never from the compiled survey, never from the shape of
 * an answer (decision 3 — a dict is a legal non-monadic shape).
 */
export function readLaneMergedScope(
  question: any,
  laneScope: MonadicLaneScopeFact | null | undefined,
  assetContextItems?: Array<{ assetId: string; title?: string; [key: string]: unknown }>,
): PerAssetScope | null {
  const laneAssetIds = laneAssetIdsForQuestion(question, laneScope)
  if (laneAssetIds.length === 0) return null

  const typeById = new Map<string, string>()
  for (const item of assetContextItems || []) {
    if (typeof item?.assetType === 'string' && item.assetType) {
      typeById.set(String(item.assetId), item.assetType)
    }
  }
  const laneTypes = new Set(
    laneAssetIds.map((assetId) => typeById.get(assetId)).filter((type): type is string => !!type),
  )

  const labelled = disambiguatedAssetReportLabels(laneAssetIds, assetContextItems)

  return {
    noun: laneTypes.size === 1 ? Array.from(laneTypes)[0].replace(/_/g, ' ') : 'asset',
    labels: laneAssetIds.map((assetId) => labelled[assetId] || assetId),
  }
}

/**
 * The price curve's role binding, keyed by SurveyJS name, from the one owner:
 * the analysis reporting plan (`reportingPlan.priceCurve.questionIdByRole`,
 * draft question ids). A historical role-less plan carries its roles only
 * there, so the threshold histograms read the same binding the curve was
 * built from. A stamped `priceRole` on the
 * question still wins inside `NumericChart`.
 */
export function priceCurveRolesBySurveyName(
  questionIdByRole: Record<string, string> | null | undefined,
  canonicalQuestionMap?: Record<string, CanonicalQuestionMapEntry>,
): Record<string, string> | undefined {
  if (!questionIdByRole) {
    return undefined
  }
  const surveyNameById = new Map<string, string>()
  for (const [surveyName, entry] of Object.entries(canonicalQuestionMap || {})) {
    if (entry?.questionId) {
      surveyNameById.set(entry.questionId, surveyName)
    }
  }
  const roles: Record<string, string> = {}
  for (const [role, questionId] of Object.entries(questionIdByRole)) {
    roles[role] = surveyNameById.get(questionId) ?? questionId
  }
  return roles
}

/**
 * The one lane-scope resolver for the card.
 *
 * An analysis that carries the study-level lane fact answers from it first.
 * A question the fact does not name keeps a per-asset scope only when it is
 * declared `per_asset` (asset-keyed without being a lane), which is the order
 * Python's `_metric_is_asset_keyed` reads the two typed facts in;
 * any other binding has no lanes. An analysis written
 * before the fact existed (`null`) falls back to the historical binding reader.
 */
export function resolveCardLaneScope(
  question: any,
  laneScope: MonadicLaneScopeFact | null | undefined,
  canonicalQuestionMap?: Record<string, CanonicalQuestionMapEntry>,
  assetContextItems?: Array<{ assetId: string; title?: string; [key: string]: unknown }>,
): PerAssetScope | null {
  if (laneScope != null) {
    const laneMerged = readLaneMergedScope(question, laneScope, assetContextItems)
    if (laneMerged) {
      return laneMerged
    }
    if (!declaresPerAssetBinding(question, canonicalQuestionMap?.[question?.name]?.assetBinding)) {
      return null
    }
  }
  return readPerAssetScope(question, canonicalQuestionMap, assetContextItems)
}

/**
 * Respondent labels fall back to the raw respondent id server-side
 * (`canonical_analysis/runtime.py`, `persona_name or respondent_label or
 * respondent_id`), so `r3` and `response_12` reach the browser as "names".
 * Matches an optional word prefix followed by digits.
 */
const RESPONDENT_ID_PATTERN = /^(?:r|p|s|sim|resp|respondent|response|twin|persona)?[\s_-]*\d+$/i
const UUID_PREFIX_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-/i

/** True when a label reads as a person's name rather than a respondent id. */
export function isPersonaName(value: string | undefined | null): boolean {
  const trimmed = (value || '').trim()
  if (!trimmed) return false
  if (!/[a-z]/i.test(trimmed)) return false
  if (UUID_PREFIX_PATTERN.test(trimmed)) return false
  return !RESPONDENT_ID_PATTERN.test(trimmed)
}

function profileKey(value: string | undefined | null): string {
  return (value || '').trim().toLowerCase()
}

/** Index the profiles by respondent id and by label, so either side of the quote resolves. */
export function buildQuoteProfileIndex(
  profiles: Array<QuoteRespondentProfile> | undefined,
): Map<string, QuoteRespondentProfile> {
  const index = new Map<string, QuoteRespondentProfile>()
  for (const profile of profiles || []) {
    for (const key of [profileKey(profile.respondentId), profileKey(profile.respondentLabel)]) {
      if (key && !index.has(key)) {
        index.set(key, profile)
      }
    }
  }
  return index
}

export interface QuoteSpeaker {
  name: string
  /**
   * The one-liner shown after the name: the speaker's label (`profile.role`
   * with `labelBasis`); for a profile stored before the speaker owner, the
   * legacy `display_profile` demographic blurb, else nothing.
   */
  blurb?: string
  imageUrl?: string
  /**
   * The saved segments this respondent belongs to, as Nest stamped them on the
   * row (`segment_labels`). Membership is never re-evaluated here.
   */
  segmentLabels?: Array<string>
}

/**
 * Index response rows by respondent id AND by respondent label, so either side
 * of a theme quote's identity resolves to the row that carries its
 * `display_profile`.
 */
export function buildQuoteRowIndex(responses: Array<any> | undefined): Map<string, any> {
  const index = new Map<string, any>()
  ;(responses || []).forEach((response, position) => {
    const keys = [
      profileKey(getResponseId(response, position)),
      profileKey(response?.respondent_label),
      profileKey(response?.respondentLabel),
    ]
    for (const key of keys) {
      if (key && !index.has(key)) {
        index.set(key, response)
      }
    }
  })
  return index
}

/** `display_profile` off a client row, or null when the row carries none. */
function readDisplayProfile(row: any): Record<string, string> | null {
  const raw = row?.display_profile
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const entries = Object.entries(raw).filter(
    ([, value]) => typeof value === 'string' && value.trim() !== '',
  )
  if (entries.length === 0) return null
  return Object.fromEntries(entries.map(([key, value]) => [key, String(value).trim()]))
}

/**
 * Resolve the speaker to show beside a quote, or null when there is nothing
 * trustworthy to show. Degrades to the quote alone rather than printing an id.
 *
 * The blurb is the speaker owner's label (`profile.role` when the profile
 * carries `labelBasis`, D16). A profile stored before that owner keeps
 * today's demographic blurb from the row's `display_profile` (`rowIndex`):
 * a versioned legacy reader, expiring 2027-01-06 or once no un-regenerated
 * report is in use, whichever comes first; then it and Nest's `display_profile`
 * projection are deleted. A legacy `profile.role` is never shown: it may be a
 * persona tagline built from source-panel answers.
 */
export function resolveQuoteSpeaker(
  quote: ThemeQuoteRef | null,
  index: Map<string, QuoteRespondentProfile>,
  rowIndex?: Map<string, any>,
): QuoteSpeaker | null {
  if (!quote) return null

  const profile =
    index.get(profileKey(quote.respondentId)) || index.get(profileKey(quote.respondentLabel))

  const name = [profile?.name, quote.respondentLabel].find((candidate) => isPersonaName(candidate))
  if (!name) return null

  const row =
    rowIndex?.get(profileKey(quote.respondentId)) ||
    rowIndex?.get(profileKey(quote.respondentLabel))
  const legacyDisplayProfile = profile?.labelBasis ? null : readDisplayProfile(row)
  const blurb = profile?.labelBasis
    ? profile.role
    : legacyDisplayProfile
      ? buildBlurbLine(legacyDisplayProfile, inferAudienceType(legacyDisplayProfile))
      : null

  const segmentLabels = Array.isArray(row?.segment_labels)
    ? row.segment_labels.filter(
        (label: unknown): label is string => typeof label === 'string' && label.trim() !== '',
      )
    : []

  return {
    name: name.trim(),
    blurb: blurb?.trim() || undefined,
    imageUrl: profile?.imageUrl?.trim() || undefined,
    ...(segmentLabels.length > 0 ? { segmentLabels } : {}),
  }
}

/** Initials for the fallback avatar, mirroring the Summary tab quote card. */
export function speakerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

function resolveCanonicalThemePackage(
  canonicalAnalysis: CanonicalAnalysisOutput | null | undefined,
  canonicalQuestionMap: Record<string, CanonicalQuestionMapEntry> | undefined,
  question: any,
): QualitativeThemePackage | null {
  const qualitativeResults = canonicalAnalysis?.qualitativeResults
  if (!qualitativeResults) return null

  const entry = canonicalQuestionMap?.[question.name]
  const candidateIds = [
    ...(entry?.qualitativeAnalysisIds || []),
    entry?.questionId,
    entry?.stableKey,
  ].filter((value): value is string => typeof value === 'string' && value.length > 0)

  for (const id of candidateIds) {
    const match = qualitativeResults[id]
    if (match) return match
  }

  const values = Object.values(qualitativeResults)
  return (
    values.find((pkg) =>
      (entry?.questionId && pkg.questionId === entry.questionId) ||
      (entry?.stableKey && pkg.stableKey === entry.stableKey),
    ) || null
  )
}

function getResponseId(response: any, index: number): string {
  return String(
    response?.respondent_id ||
      response?.respondentId ||
      response?.id ||
      `response_${index + 1}`,
  )
}

function findCitationIndex(
  quote: QualitativeQuote,
  responses: any[],
  question: any,
): number | null {
  if (quote.respondentId) {
    const byRespondent = responses.findIndex(
      (response, index) => getResponseId(response, index) === quote.respondentId,
    )
    if (byRespondent >= 0) return byRespondent
  }

  const quoteText = quote.text.trim()
  if (!quoteText) return null
  const byText = responses.findIndex((response) => {
    const value = getQuestionValue(response, question)
    return typeof value === 'string' && value.trim() === quoteText
  })
  return byText >= 0 ? byText : null
}

function buildThemeSummary(
  themePackage: QualitativeThemePackage | null,
  responses: any[],
  question: any,
): ThemeSummary | null {
  if (!themePackage?.themes?.length) return null
  return {
    themes: themePackage.themes.map((theme) => ({
      id: theme.id,
      theme: theme.title,
      description: theme.description,
      percent: theme.percent,
      quotes: theme.quotes.map((quote) => quote.text),
      quoteRefs: theme.quotes.map((quote) => ({
        text: quote.text,
        respondentId: quote.respondentId ?? undefined,
        respondentLabel: quote.respondentLabel ?? undefined,
        assetId: quote.assetId ?? undefined,
      })),
      variantShares: theme.variantShares?.length ? theme.variantShares.map(toVariantShareRef) : undefined,
      citation_indices: theme.quotes
        .map((quote) => findCitationIndex(quote, responses, question))
        .filter((index): index is number => index !== null),
    })),
  }
}

export function pickRepresentativeQuote(summary: ThemeSummary): ThemeQuoteRef | null {
  for (const theme of summary.themes) {
    const quote = theme.quoteRefs?.find((candidate) => candidate.text.trim().length > 20)
    if (quote) return { ...quote, text: quote.text.trim() }
  }
  return null
}

/**
 * The featured quote for a segment: always a verbatim from inside that segment,
 * or none at all. Never another segment's — a quote attributed to the wrong cut
 * is worse than a card with no quote on it.
 *
 * Deterministic: the in-segment pools already arrive in `quoteCandidates` order,
 * themes in package order, and a `confidence: 'high'` verbatim is preferred over
 * the first one only when one exists. Same minimum length as the unsegmented
 * picker, so a segment whose verbatims are all one-word answers shows no quote
 * rather than a different one from its neighbours.
 */
export function pickSegmentRepresentativeQuote(
  shares: Array<SegmentThemeShare>,
): ThemeQuoteRef | null {
  const seen = new Set<string>()
  const candidates: Array<QualitativeQuote> = []
  for (const share of shares) {
    for (const quote of share.quotes) {
      if (seen.has(quote.id)) continue
      seen.add(quote.id)
      if (quote.text.trim().length > 20) candidates.push(quote)
    }
  }
  if (candidates.length === 0) return null

  const chosen = candidates.find((quote) => quote.confidence === 'high') ?? candidates[0]
  return {
    text: chosen.text.trim(),
    respondentId: chosen.respondentId ?? undefined,
    respondentLabel: chosen.respondentLabel ?? undefined,
    assetId: chosen.assetId ?? undefined,
  }
}

export function QuestionCard({
  question: questionProp,
  pairedQuestion,
  questionNumber,
  segmentableQuestions,
  responses,
  populationAttributes = [],
  canonicalAnalysis,
  canonicalQuestionMap,
  assetContextItems,
  testedAssets,
  quoteRespondentProfiles,
  contentLanguage,
  layout = 'default',
  segmentationMappings = null,
  figureQuestionId = null,
}: QuestionCardProps) {
  const { t, i18n } = useTranslation('app')
  const textResponsesListLabels = useTextResponsesListLabels()
  const rankingContentLanguage = useContentLanguage(contentLanguage)
  const rankingLabels = useMemo(
    () => rankingChartLabelsForLanguage(rankingContentLanguage),
    [rankingContentLanguage],
  )
  const numericLocale = intlLocaleForUi(i18n.language)
  const numericChartLabels = useMemo(() => ({
    median: t('studyReport.numericChart.median'),
    average: t('studyReport.numericChart.average'),
    spread: t('studyReport.numericChart.spread'),
    rangePrefix: t('studyReport.numericChart.rangePrefix'),
    rangeSeparator: t('studyReport.numericChart.rangeSeparator'),
    basedOn: t('studyReport.numericChart.basedOn'),
    lowBase: t('studyReport.numericChart.lowBase'),
    noData: t('studyReport.numericChart.noData'),
    showingTop: t('studyReport.numericChart.showingTop'),
    priceRoleTooCheap: t('studyReport.numericChart.priceRoleTooCheap'),
    priceRoleCheap: t('studyReport.numericChart.priceRoleCheap'),
    priceRoleExpensive: t('studyReport.numericChart.priceRoleExpensive'),
    priceRoleTooExpensive: t('studyReport.numericChart.priceRoleTooExpensive'),
  }), [t])
  const brand = useBrandOptional()
  const chartPaletteKey = brand?.brandSettings.brandThemeConfig?.chartPaletteId ?? 'default'
  /**
   * Pre/post half of a within-subject lift pair, when the study has one.
   *
   * The compiler stamps both the element (`x_dt_tags`) and its questionMap
   * mirror; the element is read first because that is what survives a
   * re-serialized surveyJson, and the map is the fallback. Absent on every
   * questionnaire authored without a lift block, which is why the chip is
   * opt-in rather than defaulted to "pre".
   */
  const liftPair = useMemo(
    () => resolveLiftPair(questionProp, pairedQuestion, canonicalQuestionMap),
    [questionProp, pairedQuestion, canonicalQuestionMap],
  )
  const [activeHalf, setActiveHalf] = useState<'pre' | 'post' | 'lift'>(
    () => readLiftTagsFromQuestion(questionProp, canonicalQuestionMap)?.half ?? 'pre',
  )
  // The Lift view reads the post half's identity; its chart is not drawn.
  const question = liftPair
    ? (activeHalf === 'pre' ? liftPair.pre : liftPair.post)
    : questionProp
  const numericPriceRole = canonicalQuestionMap?.[question.name]?.priceRole ?? question._canonical?.priceRole
  const numericPriceCurveRoles = useMemo(
    () => priceCurveRolesBySurveyName(canonicalAnalysis?.reportingPlan?.priceCurve?.questionIdByRole, canonicalQuestionMap),
    [canonicalAnalysis, canonicalQuestionMap],
  )
  const numericCurrencyCode = question._canonical?.currencyCode
  const liftHalf = useMemo(
    () => readLiftTagsFromQuestion(question, canonicalQuestionMap),
    [question, canonicalQuestionMap],
  )
  /**
   * The Python lift rows of THIS pair, joined by question id (never by the
   * tag's measure token, which names the grid, not a row). `null` hides the
   * Lift view: no joined row, nothing to show.
   */
  const liftCard = useMemo(
    () =>
      liftPair
        ? readLiftScorecardForPair(
            canonicalAnalysis,
            {
              post: liftQuestionIdsOf(liftPair.post, canonicalQuestionMap),
              pre: liftQuestionIdsOf(liftPair.pre, canonicalQuestionMap),
            },
            // A lift stored before grid rows were paired one by one is never shown.
            { postRowCount: compiledGridRowCount(liftPair.post) },
          )
        : null,
    [canonicalAnalysis, liftPair, canonicalQuestionMap],
  )
  const isLiftView = activeHalf === 'lift' && liftCard != null
  // The lift's low base, from Python's per-row and per-lane facts (`lowBase`):
  // this card's one low-base mark for the lift, as the Summary frame carries
  // the Summary card's.
  const liftLowBase = liftCard != null && liftCard.rows.some(
    (row) => row.lowBase === true || Object.values(row.byAssetId ?? {}).some((score) => score.lowBase === true),
  )
  const [selectedSegment, setSelectedSegment] = useState<string | null>(null)
  const [hasUserSelectedSegment, setHasUserSelectedSegment] = useState(false)

  const assetSegmentQuestion = useMemo(
    () => buildAssetSegmentQuestion(responses, assetContextItems),
    [responses, assetContextItems],
  )

  /**
   * The study-level lane fact, read once. `null` on every analysis written
   * before it existed, which keeps every ordinary report on its old path.
   */
  const monadicLaneScope = (canonicalAnalysis as any)?.monadicLaneScope ?? null
  const isLaneMergedCard = isLaneMergedQuestion(question, monadicLaneScope)

  const segmentCandidates = useMemo(
    () => [
      ...segmentableQuestions
        .filter((q) => q.name !== question.name && !q.isSegmentMapping && !isLaneMergedQuestion(q, monadicLaneScope))
        .map((q) => ({ question: q, group: 'survey' as const })),
      ...populationAttributes
        .filter((attr) => attr !== question.name)
        .map((attr) => ({ question: buildPopulationAttributeQuestion(attr, segmentationMappings), group: 'population' as const })),
      ...segmentableQuestions
        .filter((q) => q.isSegmentMapping && q.name !== question.name)
        .map((q) => ({ question: q, group: 'segment' as const })),
      ...(assetSegmentQuestion && assetSegmentQuestion.name !== question.name
        ? [{ question: assetSegmentQuestion, group: 'asset' as const }]
        : []),
    ],
    [segmentableQuestions, populationAttributes, segmentationMappings, question.name, assetSegmentQuestion, monadicLaneScope],
  )

  const segmentQuality = useMemo(() => {
    return segmentCandidates.map((candidate) => {
      const distribution = aggregateSimple(candidate.question, responses)
      const uniqueCount = distribution.length
      const totalCount = distribution.reduce((sum, item) => sum + item.value, 0)
      const singletonCount = distribution.filter((item) => item.value === 1).length
      const singletonShare = uniqueCount > 0 ? singletonCount / uniqueCount : 0
      const tooManySegments = uniqueCount > CHART_LIMITS.horizontalBarMaxItems
      const tooSparse = uniqueCount >= 6 && singletonShare >= 0.7
      const hasVariance = uniqueCount >= 2
      const hasCoverage = totalCount >= 4

      return {
        ...candidate,
        uniqueCount,
        isUsable: hasVariance && hasCoverage && !tooManySegments && !tooSparse,
      }
    })
  }, [segmentCandidates, responses])

  const availableSurveySegments = useMemo(
    () => segmentQuality.filter((e) => e.group === 'survey' && e.isUsable).map((e) => e.question),
    [segmentQuality],
  )
  const availablePopulationSegments = useMemo(
    () => segmentQuality.filter((e) => e.group === 'population' && e.isUsable).map((e) => e.question),
    [segmentQuality],
  )
  const availableSegmentMappings = useMemo(
    () => segmentQuality.filter((e) => e.group === 'segment' && e.isUsable).map((e) => e.question),
    [segmentQuality],
  )
  const availableAssetSegments = useMemo(
    () => segmentQuality.filter((e) => e.group === 'asset' && e.isUsable).map((e) => e.question),
    [segmentQuality],
  )
  const hasAnyAvailableSegment = availableSurveySegments.length > 0 || availablePopulationSegments.length > 0 || availableSegmentMappings.length > 0 || availableAssetSegments.length > 0
  /**
   * Same check with the Assets group left out. A themed open end never offers
   * that group (its own incidence grid is the asset cut), so a question whose
   * only segmentable dimension is the asset shown must not show a dropdown.
   */
  const hasAnyAvailableSegmentExcludingAssets =
    availableSurveySegments.length > 0 || availablePopulationSegments.length > 0 || availableSegmentMappings.length > 0
  const canSegment = questionSupportsSegmentation(question)

  const effectiveSegment = useMemo(() => {
    if (hasUserSelectedSegment) {
      return selectedSegment
    }
    // Only default-on when this chart can actually split. A matrixdropdown
    // with a segment applied renders the "not available yet" placeholder.
    if (canSegment && availableAssetSegments.length > 0 && !isLaneMergedCard) {
      return ASSET_SEGMENT_NAME
    }
    return selectedSegment
  }, [hasUserSelectedSegment, selectedSegment, availableAssetSegments.length, canSegment, isLaneMergedCard])

  const segmentQuestion = useMemo(() => {
    if (!effectiveSegment) return undefined
    const matched = segmentCandidates.find((c) => c.question.name === effectiveSegment)
    return matched?.question
  }, [effectiveSegment, segmentCandidates])

  const textResponseProfile = useMemo(() => {
    if (question.type !== 'text') return null
    const aggregatedData = aggregateSimple(question, responses)
    const totalResponses = aggregatedData.reduce((sum, d) => sum + d.value, 0)
    const uniqueCount = aggregatedData.length
    const isCategorical = uniqueCount > 0 && uniqueCount <= 15 && totalResponses / uniqueCount >= 2
    const isActuallyNumeric = aggregatedData.length > 0 &&
      aggregatedData.every((d) => !isNaN(Number(d.rawValue || d.name)) && !String(d.rawValue || d.name).includes('$'))
    return { isCategorical, isActuallyNumeric, uniqueCount }
  }, [question, responses])

  const effectiveChartType = 'bar'

  const chartQuestion = useMemo(() => {
    const isMatrixQuestion = question.type === 'matrixdropdown' || question.type === 'matrixdynamic'
    if (!isMatrixQuestion || !Array.isArray(question.columns)) {
      return question
    }

    const chartableColumns = getChartableMatrixColumns(question)
    if (chartableColumns.length === 0 || chartableColumns.length === question.columns.length) {
      return question
    }

    return {
      ...question,
      columns: chartableColumns,
    }
  }, [question])

  const coverageQuestionId = canonicalQuestionMap?.[question.name]?.questionId ?? question._canonical?.questionId
  const responseBase = coverageQuestionId ? canonicalAnalysis?.responseCoverage?.questions[coverageQuestionId] : undefined
  const intentionallyUnasked = responseBase?.shown === 0 && responseBase.assigned > 0 && responseBase.unknown === 0 &&
    ['not_reached_after_exit', 'hidden_by_condition', 'skipped_by_route'].reduce((sum, reason) => sum + (responseBase.excluded[reason] ?? 0), 0) === responseBase.assigned

  const answeredCount = useMemo(() => {
    return responses.reduce((count, response) => {
      const value = getQuestionValue(response, question)
      // Canonical usable-answer rule: a saved
      // sentinel or structural not-applicable value is not a response.
      return count + (respondentAnswered(value, question) ? 1 : 0)
    }, 0)
  }, [responses, question])

  const prevAnsweredCountRef = useRef(answeredCount)
  const [countPulse, setCountPulse] = useState(false)
  useEffect(() => {
    if (answeredCount > prevAnsweredCountRef.current) {
      setCountPulse(true)
      const timer = setTimeout(() => setCountPulse(false), 600)
      prevAnsweredCountRef.current = answeredCount
      return () => clearTimeout(timer)
    }
    prevAnsweredCountRef.current = answeredCount
  }, [answeredCount])

  const useRadar = isRadarCandidate(chartQuestion) && !segmentQuestion
  const appChartColors = useMemo(() => {
    void chartPaletteKey
    return getAppChartPalette()
  }, [chartPaletteKey])
  const radarData = useMemo(() => {
    if (!useRadar) return null
    const data = buildRadarData(chartQuestion, responses, assetContextItems)
    if (data) {
      data.series = data.series.map((s, idx) => ({
        ...s,
        color: appChartColors[idx % appChartColors.length],
      }))
    }
    return data
  }, [chartQuestion, responses, useRadar, appChartColors, assetContextItems])

  const isTextQuestion = question.type === 'text' && !textResponseProfile?.isCategorical && !textResponseProfile?.isActuallyNumeric && question.inputType !== 'number'
  const isCommentQuestion = question.type === 'comment'
  const isTextMatrixQuestion = (question.type === 'matrixdropdown' || question.type === 'matrixdynamic') &&
    Array.isArray(question.columns) && question.columns.length > 0 &&
    question.columns.every((c: any) => {
      const ct = String(c?.cellType || c?.inputType || '').toLowerCase()
      return ct === 'text' || ct === 'comment'
    })
  const isFreeText = isTextQuestion || isCommentQuestion || isTextMatrixQuestion

  /**
   * Plots plan D3: on the survey tab a chart card draws the figure service's
   * figures (one batched request per cut for the whole tab) instead of
   * aggregating responses here. Themes, verbatims and the lift view keep their
   * own paths; outside the survey tab (no figure source) nothing changes.
   */
  const figureSplitQuestions = useMemo(
    () => segmentableQuestions.filter((q) => !isLaneMergedQuestion(q, monadicLaneScope)),
    [segmentableQuestions, monadicLaneScope],
  )
  const questionKind = resolveReportQuestionKind(question, {
    isCategoricalText: textResponseProfile?.isCategorical,
    isNumericText: textResponseProfile?.isActuallyNumeric,
  })

  /**
   * Does the chart below already print its own respondent base? Only matters
   * in `stacked` (the Summary featured slot): NumericChart's single
   * distribution ends with "Based on N responses" and MatrixChart's row/
   * column tracks print their own n, so the header's count line would only
   * repeat it. Free-text, the radar swap and Survey Data's two-column layout
   * carry no such footer, so the header keeps counting for those.
   */
  const chartPrintsOwnBase =
    layout === 'stacked' &&
    !isFreeText &&
    !(useRadar && radarData) &&
    CHART_TYPES_WITH_OWN_BASE_FOOTER.has(question.type)

  const canonicalThemePackage = useMemo(
    () => (isFreeText ? resolveCanonicalThemePackage(canonicalAnalysis, canonicalQuestionMap, question) : null),
    [isFreeText, canonicalAnalysis, canonicalQuestionMap, question],
  )

  const themeSummary = useMemo(
    () => buildThemeSummary(canonicalThemePackage, responses, question),
    [canonicalThemePackage, responses, question],
  )

  /**
   * A themed open end can segment its own theme percentages even though its
   * chart type (free text) is excluded from `SEGMENTABLE_QUESTION_TYPES`. Only
   * offered when every theme carries `quoteIds` — packages saved before that
   * field existed cannot resolve the quote/segment join in `segmentThemeShares`,
   * so they fall back to the plain unsegmented view instead of a partial or
   * misleading count.
   */
  const canSegmentThemes =
    isFreeText &&
    !!canonicalThemePackage &&
    (canonicalThemePackage.themes?.length ?? 0) > 0 &&
    canonicalThemePackage.themes.every((theme) => theme.quoteIds != null)

  /**
   * Wave 7: a themed open end (the same packages that can segment) and a
   * categorical free text draw the figure service's shares too; anything the
   * service does not draw keeps the card's own view.
   */
  const isThemedFigure = canSegmentThemes && !isLiftView
  const themeSummaryIds = useMemo(() => themeSummary?.themes.map((theme) => theme.id) ?? null, [themeSummary])
  const isCategoricalTextFigure = question.type === 'text' && textResponseProfile?.isCategorical === true && question.inputType !== 'number'
  const figureCard = useQuestionCardFigures({
    question,
    isEligible: (!isFreeText || isThemedFigure) && !isLiftView,
    canSegment: canSegment || isThemedFigure,
    segmentableQuestions: figureSplitQuestions,
    canonicalQuestionMap,
    assetContextItems,
    assignedAssetLabel: t('studyReport.questionCard.segmentAssetShown'),
    allSubsegmentsLabel: t('studyReport.questionCard.segmentAllSubsegments'),
    isLaneMergedCard,
    defaultsToAsset: canSegment && !isLaneMergedCard && !isThemedFigure,
    figureQuestionId,
    isCategoricalText: isCategoricalTextFigure,
    isThemed: isThemedFigure,
    themeIds: themeSummaryIds,
  })

  /**
   * The figure service's stamped theme cards (open-end themes plan): every
   * number, base, order and mark is Python's; the card joins each stamped
   * theme to its words by theme id. Null outside figure mode, while loading,
   * on a failure or for a view the service did not chart (the hook's drawn
   * test: no block, or none of its themes among the card's words).
   */
  const themeCardsBlock =
    figureCard.isFigureMode &&
    !figureCard.isNotChartedForView &&
    figureCard.entry.status === 'ready' &&
    figureCard.entry.question?.kind === 'theme-cards'
      ? figureCard.entry.question.themeCards ?? null
      : null

  const [isThemeListExpanded, setIsThemeListExpanded] = useState(false)
  const [openEndTab, setOpenEndTab] = useState<'themes' | 'quotes'>('themes')
  const showOpenEndTabs = layout !== 'stacked' && isFreeText && !!themeSummary?.themes.length

  /** Stamped themes in stamped order with their words; a stamped id with no words is skipped, never invented. */
  const stampedThemeEntries = useMemo(() => {
    if (!themeCardsBlock || !themeSummary) {
      return { entries: [], missingIds: [] as Array<string> }
    }
    const wordsById = new Map(themeSummary.themes.map((theme) => [theme.id, theme]))
    const entries = themeCardsBlock.themes.flatMap((card) => {
      const words = wordsById.get(card.themeId)
      return words ? [{ card, words }] : []
    })
    const missingIds = themeCardsBlock.themes.filter((card) => !wordsById.has(card.themeId)).map((card) => card.themeId)
    return { entries, missingIds }
  }, [themeCardsBlock, themeSummary])

  useEffect(() => {
    if (import.meta.env.DEV && stampedThemeEntries.missingIds.length > 0) {
      console.warn('QuestionCard - stamped themes without words, skipped', stampedThemeEntries.missingIds)
    }
  }, [stampedThemeEntries.missingIds])

  /**
   * The chosen dimension's own values, so the value select can default to the
   * largest one. An open end needs one concrete value selected (e.g. "UK"),
   * not just a dimension: unlike a chart, there is no single view that shows
   * every value of the dimension side by side.
   */
  const segmentValueOptions = useMemo(() => {
    if ((!canSegmentThemes && !isLaneMergedCard) || !segmentQuestion) return []
    return aggregateSimple(segmentQuestion, responses)
  }, [canSegmentThemes, isLaneMergedCard, segmentQuestion, responses])

  const [selectedSegmentValue, setSelectedSegmentValue] = useState<string | null>(null)
  const [hasUserSelectedValue, setHasUserSelectedValue] = useState(false)

  // A newly chosen dimension starts back at its own default value instead of
  // keeping a stale one carried over from the previous dimension.
  useEffect(() => {
    setSelectedSegmentValue(null)
    setHasUserSelectedValue(false)
  }, [segmentQuestion?.name])

  const effectiveSegmentValue = useMemo(() => {
    if (hasUserSelectedValue) return selectedSegmentValue
    if (segmentValueOptions.length === 0) return null
    // A saved segment's members ARE the cut, so that side wins over the larger
    // one; every other dimension still defaults to its largest value.
    const preferred = segmentQuestion?.preferredSegmentValue
    if (preferred != null) {
      const match = segmentValueOptions.find(
        (option) => String(option.rawValue) === String(preferred),
      )
      if (match) return String(match.rawValue)
    }
    const largest = segmentValueOptions.reduce(
      (best, option) => (option.value > best.value ? option : best),
      segmentValueOptions[0],
    )
    return largest.rawValue != null ? String(largest.rawValue) : null
  }, [
    hasUserSelectedValue,
    selectedSegmentValue,
    segmentValueOptions,
    segmentQuestion?.preferredSegmentValue,
  ])

  /** `null` for "All respondents", or when the package predates `quoteIds`. */
  const segmentThemeShareResult = useMemo(() => {
    if (!canSegmentThemes || !segmentQuestion || effectiveSegmentValue == null) return null
    const option = segmentValueOptions.find((value) => String(value.rawValue) === effectiveSegmentValue)
    return segmentThemeShares(canonicalThemePackage, responses, segmentQuestion, option)
  }, [canSegmentThemes, segmentQuestion, effectiveSegmentValue, segmentValueOptions, canonicalThemePackage, responses])

  /**
   * Themes paired with their in-segment share, re-sorted by that share once a
   * segment value is active. `index` keeps pointing at the theme's position in
   * `themeSummary.themes` so incidence-grid testids stay stable across a
   * reorder.
   */
  const orderedThemeEntries = useMemo(() => {
    if (!themeSummary) return []
    const entries = themeSummary.themes.map((theme, index) => ({
      theme,
      index,
      share: segmentThemeShareResult ? segmentThemeShareResult[index] ?? null : null,
    }))
    if (!segmentThemeShareResult) return entries
    return [...entries].sort((a, b) => (b.share?.percent ?? 0) - (a.share?.percent ?? 0))
  }, [themeSummary, segmentThemeShareResult])

  const representativeQuote = useMemo(() => {
    if (segmentThemeShareResult) return pickSegmentRepresentativeQuote(segmentThemeShareResult)
    return themeSummary ? pickRepresentativeQuote(themeSummary) : null
  }, [themeSummary, segmentThemeShareResult])

  const cardLaneScope = useMemo(
    () => resolveCardLaneScope(question, monadicLaneScope, canonicalQuestionMap, assetContextItems),
    [question, monadicLaneScope, canonicalQuestionMap, assetContextItems],
  )

  /**
   * Lane-merged segmentation filters twins first, then unfolds. The inner
   * chart never receives `segmentQuestion` as a grouping axis.
   */
  const laneChartResponses = useMemo(() => {
    if (!isLaneMergedCard || !segmentQuestion || effectiveSegmentValue == null) {
      return responses
    }
    return responsesMatchingSegmentValue(responses, segmentQuestion, effectiveSegmentValue)
  }, [isLaneMergedCard, segmentQuestion, effectiveSegmentValue, responses])

  const labelById = useMemo(
    () =>
      disambiguatedAssetReportLabels(
        laneAssetIdsForQuestion(question, monadicLaneScope),
        withVersionTitles(
          assetContextItems,
          testedAssets,
          respondentWordingFor(rankingContentLanguage),
        ),
      ),
    [question, monadicLaneScope, assetContextItems, testedAssets, rankingContentLanguage],
  )

  /**
   * Per-lane option shares for a lane-merged choice question (checkbox,
   * radiogroup, dropdown, boolean, or a text that charts as categorical).
   *
   * Without this the answer dict reaches `aggregateSimple` whole and comes out
   * as a single bar labelled `[object Object]` whose value is the respondent
   * count. Ranking, matrix, numeric and open text pass the fact into their
   * native charts, which unfold and reuse their existing aggregators.
   */
  const laneMergedShares = useMemo<LaneMergedChoiceShares | null>(() => {
    const isChoiceFamily =
      question.type === 'checkbox' ||
      question.type === 'radiogroup' ||
      question.type === 'dropdown' ||
      question.type === 'boolean' ||
      (question.type === 'text' && textResponseProfile?.isCategorical && !figureCard.isDeclinedByService)
    if (!isChoiceFamily) return null
    return laneMergedChoiceShares(question, laneChartResponses, monadicLaneScope, (assetId) =>
      labelById[assetId] || assetId,
    )
  }, [question, laneChartResponses, monadicLaneScope, labelById, textResponseProfile, figureCard.isDeclinedByService])

  const responsesPerAsset = useMemo(() => {
    const scope = cardLaneScope
    if (!scope) {
      return null
    }
    if (laneMergedShares) {
      const base = uniformPositiveBase(laneMergedShares.series)
      return base == null ? null : { count: base, noun: scope.noun }
    }
    return { count: answeredCount, noun: scope.noun }
  }, [cardLaneScope, laneMergedShares, answeredCount])

  /**
   * Lanes with per-theme incidence, in the deck's order (`QualThemeIncidenceGrid`).
   * Labelled from the asset context so the cells match the scope line. One
   * lane is no comparison, so the cells only render for two or more.
   */
  const incidenceVariants = useMemo(() => {
    const variants = themeSummary ? collectIncidenceVariants(themeSummary.themes) : []
    if (variants.length <= 1) return []
    // A reason-for-choice package groups people by the asset they chose, so
    // its cells read "Chose X", never as answers about X.
    const chose = canonicalThemePackage?.variantBasis === 'chosen_asset'
    return variants.map((variant) => {
      const label = formatAssetReportLabel(variant.label, assetContextItems, [variant.variantId])
      return { ...variant, label: chose ? `Chose ${label}` : label }
    })
  }, [themeSummary, assetContextItems, canonicalThemePackage])

  const quoteVersions = useMemo(
    () => themeCardsBlock?.layout === 'versions'
      ? themeCardsBlock.versions.map((version, position) => ({
          id: version.versionId,
          label: version.label ?? t('studyReport.questionCard.laneFallbackTitle', { n: position + 1 }),
        }))
      : incidenceVariants.map((variant) => ({ id: variant.variantId, label: variant.label })),
    [themeCardsBlock, incidenceVariants, t],
  )

  const scopedQuoteIds = useMemo(
    () => segmentThemeShareResult
      ? new Set(segmentThemeShareResult.flatMap((share) => share.quotes.map((quote) => quote.id)))
      : null,
    [segmentThemeShareResult],
  )

  const quoteProfileIndex = useMemo(
    () => buildQuoteProfileIndex(quoteRespondentProfiles),
    [quoteRespondentProfiles],
  )

  const quoteRowIndex = useMemo(() => buildQuoteRowIndex(responses), [responses])

  const representativeSpeaker = useMemo(
    () => resolveQuoteSpeaker(representativeQuote, quoteProfileIndex, quoteRowIndex),
    [representativeQuote, quoteProfileIndex, quoteRowIndex],
  )
  const { displayUrl: representativeSpeakerThumbUrl, handleImageError: handleRepresentativeSpeakerImageError } =
    usePersonaThumbFallback(representativeSpeaker?.imageUrl, 64)

  /** The stamped theme cards: words joined by id, each figure inside its card, 6 then "Show N more themes". */
  const renderStampedThemeCards = (block: StudyFigureThemeCards) => {
    const { entries } = stampedThemeEntries
    const otherTitle = entries.find((entry) => entry.card.isOther)?.words.theme ?? null
    const hiddenCount = Math.max(0, entries.length - THEME_CARDS_VISIBLE)
    const visible = isThemeListExpanded ? entries : entries.slice(0, THEME_CARDS_VISIBLE)
    return (
      <>
        <div className={cn(layout === 'stacked' ? 'space-y-6' : 'overflow-hidden rounded-2xl border border-border bg-background divide-y divide-border')}>
          {visible.map(({ card, words }, position) => (
            <div
              key={card.themeId}
              className={cn(layout === 'stacked' ? 'bg-[hsl(var(--report-card))] rounded-2xl p-8' : 'px-5 py-4')}
              data-testid={`question-card-theme-card-${position}`}
            >
              <Heading level={4} className={cn('font-sans text-[14px] text-foreground', layout === 'stacked' ? 'mb-2 font-normal' : 'font-semibold')}>
                {words.theme}
              </Heading>
              <Text className="text-foreground leading-relaxed">{words.description}</Text>
              <ThemeCardFigure
                cards={block}
                card={card}
                index={position}
                showsOverlapInfo={position === 0}
                otherTitle={otherTitle}
                displayBars={layout !== 'stacked'}
              />
            </div>
          ))}
        </div>
        {hiddenCount > 0 && (
          <button
            type="button"
            className="rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
            onClick={() => setIsThemeListExpanded((isExpanded) => !isExpanded)}
            aria-expanded={isThemeListExpanded}
            data-testid="question-card-theme-show-more"
          >
            {isThemeListExpanded
              ? t('studyReport.questionCard.themeFigure.showFewer')
              : t('studyReport.questionCard.themeFigure.showMore', { count: hiddenCount })}
          </button>
        )}
        {layout !== 'stacked' && (
          <Text size="xs" color="muted" data-testid="question-card-theme-share-note">
            {t('studyReport.questionCard.themeFigure.shareNote')}
          </Text>
        )}
      </>
    )
  }

  const renderInsightCards = () => {
    if (!themeSummary?.themes.length) return null
    const totalCitations = themeSummary.themes.reduce((s, t) => s + t.citation_indices.length, 0)
    // In figure mode the service stamps every number: the cards print none of
    // the browser's, only the stamped figures once they arrive.
    const isThemeFigure = figureCard.isFigureMode
    // Under a filter the pooled quote may come from outside the group: hidden.
    const showsRepresentativeQuote = !(isThemeFigure && figureCard.activeValueId != null)

    return (
      <div className="space-y-6">
        {isThemeFigure && themeCardsBlock == null && (
          <FigureEntryStatus
            entry={figureCard.entry}
            onRetry={figureCard.retry}
            isNotCharted={figureCard.isNotChartedForView}
            showsLoading={false}
          />
        )}
        {isThemeFigure && themeCardsBlock != null ? renderStampedThemeCards(themeCardsBlock) : orderedThemeEntries.map(({ theme, index: i, share }) => {
          const pct = share
            ? Math.round(share.percent)
            : typeof theme.percent === 'number'
              ? Math.round(theme.percent)
              : totalCitations > 0
                ? Math.round((theme.citation_indices.length / totalCitations) * 100)
                : 0
          const shareById = new Map(
            (theme.variantShares || []).map((variantShare) => [variantShare.variantId, variantShare]),
          )
          // variantShares are computed over ALL respondents; once a segment
          // value is active they no longer sit beside a filtered pooled
          // percent, so the grid hides until the user goes back to "All
          // respondents". Recomputing per-asset shares in-segment is a
          // follow-up, not v1.
          const showIncidence = !isThemeFigure && !share && incidenceVariants.length > 0 && shareById.size > 0
          return (
            <div key={i} className="bg-[hsl(var(--report-card))] rounded-2xl p-8">
              <Heading level={4} className="mb-2 font-sans text-[14px] font-normal text-foreground">{theme.theme}</Heading>
              <Text className="text-foreground leading-relaxed">{theme.description}</Text>
              {!isThemeFigure && (pct > 0 || share) && (
                /* With lanes shown, the pooled share is over every lane's
                   answers, not over respondents. With a segment active, the
                   base is the segment's own verbatim count. */
                <Text size="sm" color="muted" className="mt-3">
                  {share
                    ? `~${pct}% of ${share.base} response${share.base === 1 ? '' : 's'}`
                    : `~${pct}% of ${showIncidence ? 'all answers' : 'responses'}`}
                </Text>
              )}
              {showIncidence && (
                <div
                  data-testid={`question-card-theme-incidence-${i}`}
                  className="mt-3 grid gap-3"
                  style={{ gridTemplateColumns: `repeat(${incidenceVariants.length}, minmax(0, 1fr))` }}
                >
                  {incidenceVariants.map((variant) => (
                    <div
                      key={variant.variantId}
                      data-testid={`question-card-theme-incidence-cell-${i}-${variant.variantId}`}
                      className="min-w-0"
                    >
                      <Text size="xs" color="muted" className="leading-tight truncate" title={variant.label}>
                        {variant.label}
                      </Text>
                      <Text size="sm" weight="semibold" className="leading-tight tabular-nums">
                        {formatIncidenceCell(shareById.get(variant.variantId))}
                      </Text>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
        {showsRepresentativeQuote && representativeQuote && (
          <div className="my-6">
            <blockquote
              data-testid="question-card-quote"
              className="border-l-2 border-foreground pl-5 text-[15px] text-foreground leading-relaxed italic"
            >
              &ldquo;{representativeQuote.text}&rdquo;
            </blockquote>
            {/* Speaker row mirrors the Summary tab quote card: portrait (or
                initials) then name over role. Suppressed entirely when the only
                label available is a raw respondent id. */}
            {representativeSpeaker && (
              <div
                data-testid="question-card-quote-speaker-row"
                className="flex items-center gap-3 pl-5 mt-3"
              >
                {representativeSpeakerThumbUrl ? (
                  <img
                    src={representativeSpeakerThumbUrl}
                    alt={representativeSpeaker.name}
                    data-testid="question-card-quote-speaker-avatar"
                    className="w-12 h-12 rounded-full object-cover flex-shrink-0 border border-border/40"
                    onError={() => handleRepresentativeSpeakerImageError()}
                  />
                ) : (
                  <span
                    data-testid="question-card-quote-speaker-initials"
                    className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-medium border border-foreground/10 bg-primary/10 dark:bg-primary/15 text-foreground/75"
                  >
                    {speakerInitials(representativeSpeaker.name)}
                  </span>
                )}

                {/* One line, `Name, blurb`: the name carries the weight, the
                    blurb stays muted, and the whole line truncates together
                    rather than wrapping under the portrait. */}
                <div className="flex min-w-0 flex-1 items-baseline truncate">
                  <span
                    data-testid="question-card-quote-speaker-name"
                    className="text-sm font-medium text-foreground"
                  >
                    {representativeSpeaker.name}
                  </span>
                  {representativeSpeaker.blurb && (
                    <span
                      data-testid="question-card-quote-speaker-blurb"
                      className="text-xs text-muted-foreground truncate"
                    >
                      , {representativeSpeaker.blurb}
                    </span>
                  )}
                  {representativeSpeaker.segmentLabels && (
                    /* Saved segments the speaker belongs to. Capped at two:
                       membership is many-to-many, and a speaker in five
                       segments would push the name off the line. */
                    <span
                      data-testid="question-card-quote-speaker-segments"
                      className="ml-2 inline-flex shrink-0 items-center gap-1"
                    >
                      {representativeSpeaker.segmentLabels.slice(0, 2).map((label, position) => (
                        <SubsegmentChip
                          key={label}
                          label={label}
                          testId={`question-card-quote-speaker-segment-${position}`}
                        />
                      ))}
                      {representativeSpeaker.segmentLabels.length > 2 && (
                        <SubsegmentOverflowChip
                          count={representativeSpeaker.segmentLabels.length - 2}
                          testId="question-card-quote-speaker-segment-overflow"
                        />
                      )}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  /**
   * A lane-merged multi-select: options across, one series per tested asset.
   *
   * `ChartJsGroupedBar` slices silently at its label and series caps, so a
   * question that would exceed either shows the full table instead — hidden
   * truncation is the one outcome the display rules refuse (8.2 / 8.4). No new
   * chart component either way. The caps are checked on the axes the chart
   * actually draws: stacked mode swaps them (lanes become labels, options
   * become series). `LaneMediaBars` does not slice, so it needs no check.
   */
  const renderLaneMergedChoice = (shares: LaneMergedChoiceShares) => {
    const painted = shares.series.filter((entry) => entry.base > 0)
    const empty = shares.series.filter((entry) => entry.base === 0)
    const isScale = shouldStackLaneMergedChoice(question)
    const chartLabels = isScale
      ? painted.map((entry) => entry.label)
      : shares.labels
    const chartSeries = isScale
      ? shares.labels.map((label, optionIndex) => ({
          label,
          values: painted.map((entry) => entry.values[optionIndex] ?? 0),
        }))
      : painted.map((entry) => ({ label: entry.label, values: entry.values }))
    const mediaPreviews = isScale
      ? previewsForAssets(painted.map((entry) => entry.assetId), testedAssets)
      : null
    const mediaRows = mediaPreviews
      ? painted.flatMap((entry, index) => {
          const preview = mediaPreviews[index]
          return preview ? [{ id: entry.assetId, label: entry.label, preview }] : []
        })
      : null
    const drawsGroupedBar = !mediaRows && painted.length > 0
    const exceedsChart =
      drawsGroupedBar &&
      (chartLabels.length > CHART_LIMITS.groupedBarMaxLabels ||
        chartSeries.length > CHART_LIMITS.groupedBarMaxSeries)

    // Names already live on the axis or in the color legend. A second roster
    // is only worth printing when the lanes were not asked of the same people,
    // because that is the one fact neither the axis nor "N responses" states.
    const answeredLanes = shares.series.filter((entry) => entry.base > 0)
    const baseLine = uniformPositiveBase(shares.series) != null || answeredLanes.length === 0 ? null : (
      <p
        data-testid="question-card-lane-merged-bases"
        className="mt-3 text-xs text-muted-foreground"
      >
        {answeredLanes.map((entry) =>
          t('studyReport.questionCard.laneBase', { label: entry.label, base: entry.base }),
        ).join(' · ')}
      </p>
    )

    if (exceedsChart) {
      return (
        <div data-testid="question-card-lane-merged-table">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="py-1 pr-3 text-left font-medium text-muted-foreground">Option</th>
                {shares.series.map((entry) => (
                  <th
                    key={entry.assetId}
                    className="py-1 pl-3 text-right font-medium text-muted-foreground"
                  >
                    {entry.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shares.labels.map((label, index) => (
                <tr key={shares.optionValues[index]}>
                  <td className="py-1 pr-3 text-foreground">{label}</td>
                  {shares.series.map((entry) => (
                    <td
                      key={entry.assetId}
                      data-testid={`question-card-lane-merged-cell-${entry.assetId}-${shares.optionValues[index]}`}
                      className="py-1 pl-3 text-right tabular-nums text-foreground"
                    >
                      {entry.base > 0 ? `${Math.round(entry.values[index])}%` : '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {baseLine}
        </div>
      )
    }

    return (
      <div data-testid="question-card-lane-merged-chart">
        {mediaRows ? (
          <LaneMediaBars rows={mediaRows} segments={chartSeries} />
        ) : drawsGroupedBar ? (
          <div className="h-80">
            <ChartJsGroupedBar
              labels={chartLabels}
              series={chartSeries}
              orientation="horizontal"
              stacked={isScale}
              yAxisMax={100}
              showLegend
              showYGrid
              useCategoricalPalette
            />
          </div>
        ) : null}
        {empty.map((entry) => (
          <p
            key={entry.assetId}
            className="text-sm text-muted-foreground"
            data-testid={`question-card-empty-lane-${entry.assetId}`}
          >
            {entry.label}: {t('studyReport.questionCard.emptyLane')}
          </p>
        ))}
        {baseLine}
      </div>
    )
  }

  function headerDenominator(): number | null {
    if (responsesPerAsset != null) {
      return responsesPerAsset.count
    }
    if (
      figureCard.isFigureMode &&
      figureCard.entry.status === 'ready'
    ) {
      const total = figureCard.entry.groups.find((group) => group.kind === 'total')
      if (total?.drawn && total.n > 0) {
        return total.n
      }
    }
    if (layout === 'stacked') {
      if (chartPrintsOwnBase) {
        return null
      }
      return answeredCount
    }
    if (!question.description) {
      return answeredCount
    }
    return null
  }
  const visibleDenominator = headerDenominator()

  const renderChart = () => {
    if (isFreeText && themeSummary?.themes.length) {
      return renderInsightCards()
    }

    if (figureCard.isFigureMode) {
      return (
        <QuestionFigureView
          entry={figureCard.entry}
          isCut={figureCard.isCut}
          isNotCharted={figureCard.isNotChartedForView}
          colors={appChartColors}
          onRetry={figureCard.retry}
          rankingLabels={rankingLabels}
          visibleDenominator={visibleDenominator}
        />
      )
    }

    if (laneMergedShares) {
      return renderLaneMergedChoice(laneMergedShares)
    }

    const chartSegmentQuestion = isLaneMergedCard ? undefined : segmentQuestion
    const chartResponses = isLaneMergedCard ? laneChartResponses : responses
    const emptyLaneLabel = t('studyReport.questionCard.emptyLane')

    switch (question.type) {
      case 'radiogroup':
      case 'dropdown':
      case 'boolean':
        return <SingleChoiceChart question={question} segmentQuestion={chartSegmentQuestion} responses={chartResponses} type={effectiveChartType} colors={appChartColors} assetContextItems={assetContextItems} monadicLaneScope={monadicLaneScope} emptyLaneLabel={emptyLaneLabel} />
      case 'checkbox':
        return <SingleChoiceChart question={question} segmentQuestion={chartSegmentQuestion} responses={chartResponses} type="bar" colors={appChartColors} assetContextItems={assetContextItems} monadicLaneScope={monadicLaneScope} emptyLaneLabel={emptyLaneLabel} />
      case 'rating':
        return <NumericChart question={question} segmentQuestion={chartSegmentQuestion} responses={chartResponses} colors={appChartColors} assetContextItems={assetContextItems} monadicLaneScope={monadicLaneScope} emptyLaneLabel={emptyLaneLabel} currencyCode={numericCurrencyCode} locale={numericLocale} labels={numericChartLabels} priceRole={numericPriceRole} priceCurveRoles={numericPriceCurveRoles} />
      case 'text': {
        if (question.inputType === 'number') return <NumericChart question={question} responses={chartResponses} colors={appChartColors} monadicLaneScope={monadicLaneScope} assetContextItems={assetContextItems} emptyLaneLabel={emptyLaneLabel} currencyCode={numericCurrencyCode} locale={numericLocale} labels={numericChartLabels} priceRole={numericPriceRole} priceCurveRoles={numericPriceCurveRoles} />
        // Python's categorical rule is the single owner: a categorical free text
        // the service declined lists its answers (the browser rule only asks).
        if (textResponseProfile?.isCategorical && !figureCard.isDeclinedByService) return <SingleChoiceChart question={question} segmentQuestion={chartSegmentQuestion} responses={chartResponses} type={effectiveChartType} colors={appChartColors} assetContextItems={assetContextItems} monadicLaneScope={monadicLaneScope} emptyLaneLabel={emptyLaneLabel} />
        return <TextResponsesList labels={textResponsesListLabels} question={question} responses={chartResponses} themeSummary={themeSummary} assetContextItems={assetContextItems} monadicLaneScope={monadicLaneScope} />
      }
      case 'comment':
        return <TextResponsesList labels={textResponsesListLabels} question={question} responses={chartResponses} themeSummary={themeSummary} assetContextItems={assetContextItems} monadicLaneScope={monadicLaneScope} />
      case 'matrix':
      case 'matrixdropdown':
      case 'matrixdynamic': {
        if (isTextMatrixQuestion) {
          return <TextResponsesList labels={textResponsesListLabels} question={question} responses={chartResponses} themeSummary={themeSummary} assetContextItems={assetContextItems} monadicLaneScope={monadicLaneScope} />
        }
        if (useRadar && radarData) {
          return (
            /* The polygon is bound by the SHORTER canvas axis (minus the legend and the
               point labels), so a squat box wastes the card width. Track the column with a
               capped 4:3 box instead of a fixed 320px height: the radar grows with the
               card, stays centred, and shrinks cleanly on narrow screens. */
            <div className="mx-auto aspect-[4/3] max-h-[30rem] w-full max-w-3xl">
              <RadarChart labels={radarData.labels} series={radarData.series} min={radarData.min} max={radarData.max} height="100%" />
            </div>
          )
        }
        return <MatrixChart question={chartQuestion} segmentQuestion={chartSegmentQuestion} responses={chartResponses} colors={appChartColors} assetContextItems={assetContextItems} monadicLaneScope={monadicLaneScope} emptyLaneLabel={emptyLaneLabel} />
      }
      case 'ranking':
        return <RankingChart question={question} segmentQuestion={chartSegmentQuestion} responses={chartResponses} colors={appChartColors} assetContextItems={assetContextItems} labels={rankingLabels} monadicLaneScope={monadicLaneScope} emptyLaneLabel={emptyLaneLabel} />
      default:
        return <div className="py-8 text-sm text-muted-foreground">Unsupported question type: {question.type}</div>
    }
  }

  const isChartQuestion = !isFreeText || !themeSummary?.themes.length

  /**
   * Lane-merged cards already split by asset. Segmenting them filters twins
   * then unfolds; the Assets group would re-cut by the same lanes.
   */
  const showSegmentControl = isLaneMergedCard
    ? hasAnyAvailableSegmentExcludingAssets
    : canSegment
      ? hasAnyAvailableSegment
      : canSegmentThemes && hasAnyAvailableSegmentExcludingAssets

  const segmentDropdown = figureCard.isFigureMode ? (
    figureCard.dimensions.length > 0 || figureCard.metaStatus === 'failed' ? (
      <FigureSegmentControls figures={figureCard} />
    ) : null
  ) : showSegmentControl ? (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-[hsl(var(--report-surface))] rounded-full text-xs">
      <span className="text-foreground/70">Segment</span>
      <Select
        value={effectiveSegment || 'none'}
        onValueChange={(val) => {
          setHasUserSelectedSegment(true)
          setSelectedSegment(val === 'none' ? null : val)
        }}
      >
        <SelectTrigger
          data-testid="question-card-segment-select"
          className="h-auto border-none bg-transparent p-0 text-xs font-medium shadow-none focus:ring-0 w-auto min-w-[100px]"
        >
          <SelectValue placeholder="All respondents" />
        </SelectTrigger>
        <SelectContent className="min-w-[360px] max-h-[300px]">
          <SelectItem value="none">All respondents</SelectItem>
          {availableSegmentMappings.length > 0 && (
            <>
              {/* Segments leads every dropdown on the page now, so it takes the
                  borderless header style the first group after "All respondents"
                  always uses (previously Questions' style, when Questions led). */}
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground" data-testid="segment-group-segments">Segments</div>
              {availableSegmentMappings.map((segQ: any) => (
                <SelectItem key={segQ.name} value={segQ.name} data-testid={`segment-option-segment-${segQ.name}`}>{segQ.title || segQ.name}</SelectItem>
              ))}
            </>
          )}
          {availableSurveySegments.length > 0 && (
            <>
              <div className={cn(
                'px-2 py-1.5 text-xs font-medium text-muted-foreground',
                availableSegmentMappings.length > 0 && 'border-t mt-1 pt-2',
              )} data-testid="segment-group-questions">Questions</div>
              {availableSurveySegments.map((q) => (
                <SelectItem key={q.name} value={q.name} data-testid={`segment-option-question-${q.name}`}>{q.title || q.name}</SelectItem>
              ))}
            </>
          )}
          {availablePopulationSegments.length > 0 && (
            <>
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-t mt-1 pt-2" data-testid="segment-group-population">Population</div>
              {availablePopulationSegments.map((attrQ: any) => (
                <SelectItem key={attrQ.name} value={attrQ.name} data-testid={`segment-option-population-${attrQ.name}`}>{attrQ.title || attrQ.name}</SelectItem>
              ))}
            </>
          )}
          {canSegment && availableAssetSegments.length > 0 && !isLaneMergedCard && (
            <>
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-t mt-1 pt-2" data-testid="segment-group-assets">Assets</div>
              {availableAssetSegments.map((assetQ: any) => (
                <SelectItem
                  key={assetQ.name}
                  value={assetQ.name}
                  data-testid={`segment-option-asset-${assetQ.name}`}
                >
                  {assetQ.title || assetQ.name}
                </SelectItem>
              ))}
            </>
          )}
        </SelectContent>
      </Select>
    </div>
  ) : null

  /**
   * The chosen dimension's value, for a themed open end only: a chart
   * question already shows every value of the dimension side by side, but a
   * theme card needs one concrete cut to compute a single filtered percent.
   */
  const segmentValueSelect = !figureCard.isFigureMode && (canSegmentThemes || isLaneMergedCard) && segmentQuestion && segmentValueOptions.length > 0 ? (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-[hsl(var(--report-surface))] rounded-full text-xs">
      <span className="text-foreground/70">Value</span>
      <Select
        value={effectiveSegmentValue ?? undefined}
        onValueChange={(val) => {
          setHasUserSelectedValue(true)
          setSelectedSegmentValue(val)
        }}
      >
        <SelectTrigger
          data-testid="question-card-segment-value-select"
          className="h-auto border-none bg-transparent p-0 text-xs font-medium shadow-none focus:ring-0 w-auto min-w-[80px]"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="min-w-[220px] max-h-[300px]">
          {segmentValueOptions.map((option) => (
            <SelectItem
              key={String(option.rawValue)}
              value={String(option.rawValue)}
              data-testid={`segment-value-option-${option.rawValue}`}
            >
              {option.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  ) : null

  if (intentionallyUnasked) return (
    <section className="rounded-2xl bg-[hsl(var(--report-card))] p-6" data-testid="question-not-reached">
      <h3 className="font-semibold">{question.title || question.name}</h3>
      <p className="mt-2 text-sm text-muted-foreground">Not asked: no respondents reached this step.</p>
    </section>
  )

  return (
    /* Two columns from xl up: question on the left, chart on the right. The
       chart column is minmax(0,1fr) so wide tables shrink instead of blowing
       out the row. Below xl it stacks, which is the original layout. */
    <div
      data-layout={layout}
      className={cn(
        'grid grid-cols-1 gap-3',
        layout !== 'stacked' && 'xl:grid-cols-[320px_minmax(0,1fr)] xl:gap-6',
      )}
    >
      {/* Question header — its own box in the left column. Sticky within this
          question's grid row, so it pins while the chart scrolls and is
          released by the next question rather than by any scroll listener. */}
      <div
        className={cn(
          'flex flex-col gap-1 rounded-2xl px-1',
          layout !== 'stacked' &&
            'xl:sticky xl:top-6 xl:self-start xl:bg-[hsl(var(--report-card))] xl:px-5 xl:py-4',
        )}
      >
        {/* The Summary frame around a stacked chart already names the question
            type, so repeating it there would be the same fact twice. Survey
            Data (`default`) has no such frame and prints the type above the
            question. */}
        {questionKind && layout !== 'stacked' && (
          <span
            data-testid="question-card-type-chip"
            className="my-0.5 inline-flex w-fit items-center rounded-full border border-border bg-muted px-[10px] py-[3px] text-[11px] font-medium leading-[1.4] text-foreground"
          >
            {reportQuestionKindLabel(questionKind)}
          </span>
        )}
        <div className="flex items-start gap-3">
          {/* font-sans, not the Heading default font-display: the question
              reads as UI here, not as an editorial headline. Using the token
              rather than Geist by name keeps white-labelled themes on their
              own brand sans (--font-geist-sans, set by data-font). */}
          <Heading
            level={3}
            size="base"
            weight="semibold"
            className="font-sans text-foreground"
          >
            {question.title || question.name}
          </Heading>
          {questionNumber != null && (
            /* Optically centred on the FIRST line of the title (text-base's
               default 1.5rem line-height) rather than on the whole block, so
               a title that wraps keeps its number at the top: (24px line -
               21px badge) / 2. */
            <span className="inline-flex items-center justify-center w-[21px] h-[21px] mt-[1.5px] rounded-full bg-[hsl(var(--report-surface))] text-foreground/60 text-[10px] font-semibold shrink-0">
              {questionNumber}
            </span>
          )}
        </div>
        {/* Only when fewer people answered than took the study (routing or
            skips): the typed counts in reader words, "20 of 100 people answered".
            When everyone answered, the response count below already says it. */}
        {responseBase && !selectedSegment && !segmentQuestion && !figureCard.activeDimension &&
          responseBase.assigned > 0 && responseBase.answered < responseBase.assigned && (
          <p className="text-xs text-muted-foreground" data-testid="question-response-base">
            {t('studyReport.questionCard.answeredOfTotal', {
              answered: responseBase.answered,
              total: responseBase.assigned,
            })}
          </p>
        )}
        {responsesPerAsset != null && (
          /* One sample-size line. The noun is the asset type the lanes share
             (`ad`, `concept`, `landing page`), or `asset` when they mix.
             Twin-monadic reaches this through `resolveCardLaneScope`. */
          <span
            data-testid="question-card-asset-scope"
            className={cn(
              'text-sm text-muted-foreground leading-[1.6] transition-colors duration-500',
              countPulse && 'text-primary',
            )}
          >
            {t('studyReport.questionCard.responsesPerAsset', {
              count: responsesPerAsset.count,
              noun: responsesPerAsset.noun,
            })}
          </span>
        )}
        {layout === 'stacked' ? (
          responsesPerAsset == null && !chartPrintsOwnBase && (
            <span
              data-testid="question-card-response-count"
              className={cn(
                'text-sm text-muted-foreground leading-[1.6] transition-colors duration-500',
                countPulse && 'text-primary',
              )}
            >
              {answeredCount} response{answeredCount !== 1 ? 's' : ''}
            </span>
          )
        ) : (
          (question.description || responsesPerAsset == null) && (
            <span className={cn(
              "text-sm text-muted-foreground leading-[1.6] transition-colors duration-500",
              countPulse && "text-primary"
            )}>
              {question.description || `${answeredCount} response${answeredCount !== 1 ? 's' : ''}`}
            </span>
          )
        )}
        {themeCardsBlock?.isSaturated && (
          <span className="text-xs text-muted-foreground" data-testid="question-card-theme-saturated">
            {t('studyReport.questionCard.themeFigure.saturated')}
          </span>
        )}
      </div>

      {/* Chart card — report card surface: subtle gray in light, themed card in dark
          (never the Fairgen action-green tint). `stacked` (the Summary featured
          slot) drops the panel chrome: the Summary frame around it already
          supplies the card, so this would be a card inside a card. */}
      <div
        className={cn(
          'rounded-2xl',
          layout !== 'stacked' && 'bg-[hsl(var(--report-card))] p-6',
        )}
      >
        {/* Card toolbar: segment picker left, lift chip / pre-post toggle right.
            Rendered only when it carries something, so an untagged question
            keeps the chart flush against the top of the card exactly as before. */}
        {(liftHalf || liftPair || showOpenEndTabs || ((isChartQuestion || canSegmentThemes) && segmentDropdown)) && (
          <div className="flex items-start justify-between gap-3 mb-6">
            <div className="flex items-center gap-2">
              {(isChartQuestion || canSegmentThemes) ? segmentDropdown : null}
              {segmentValueSelect}
            </div>
            <div className="flex items-center gap-2">
              {showOpenEndTabs && (
                <div role="tablist" aria-label={t('studyReport.questionCard.openEnd.quotes')} className="inline-flex rounded-full border border-border bg-[hsl(var(--report-surface))] p-0.5" data-testid="question-card-open-end-tabs">
                  {(['themes', 'quotes'] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      role="tab"
                      aria-selected={openEndTab === tab}
                      data-testid={`question-card-open-end-tab-${tab}`}
                      onClick={() => setOpenEndTab(tab)}
                      className={cn(
                        'rounded-full px-3 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        openEndTab === tab ? 'bg-background font-semibold text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {t(`studyReport.questionCard.openEnd.${tab}`)}
                    </button>
                  ))}
                </div>
              )}
              {liftPair && (
                <div
                  className="inline-flex items-center gap-1"
                  data-testid={`report-question-pre-post-toggle-${liftPair.measure}`}
                >
                  {(liftCard ? ['pre', 'post', 'lift'] : ['pre', 'post']).map((view) => {
                    const half = view as 'pre' | 'post' | 'lift'
                    const isActive = activeHalf === half
                    return (
                      <button
                        key={half}
                        type="button"
                        data-testid={`report-question-pre-post-${half}-${liftPair.measure}`}
                        onClick={() => setActiveHalf(half)}
                        className={cn(
                          'rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors',
                          isActive
                            ? 'bg-foreground text-background'
                            : 'border border-border text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {half === 'pre' ? 'Pre' : half === 'post' ? 'Post' : t('studyReport.lift')}
                      </button>
                    )
                  })}
                </div>
              )}
              {liftHalf && !isLiftView && (
                <LiftHalfChip
                  half={liftHalf.half}
                  testId={`report-question-lift-${liftHalf.half}-${question.name}`}
                />
              )}
            </div>
          </div>
        )}
        {isLiftView && liftCard ? (
          <>
            <LiftView
              scorecard={liftCard}
              itemTags={canonicalAnalysis?.itemTags ?? {}}
              showHeading={false}
            />
            {liftLowBase && (
              <Text
                size="sm"
                data-testid="question-card-lift-low-base"
                className="text-[11px] text-muted-foreground leading-snug mt-1"
              >
                {t('studyReport.numericChart.lowBase')}
              </Text>
            )}
          </>
        ) : showOpenEndTabs && canonicalThemePackage ? (
          <div role="tabpanel" aria-label={t(`studyReport.questionCard.openEnd.${openEndTab}`)}>
            {openEndTab === 'themes' ? renderChart() : (
              <OpenEndQuoteGallery
                themePackage={canonicalThemePackage}
                versions={quoteVersions}
                scopedQuoteIds={scopedQuoteIds}
                hasActiveFigureFilter={figureCard.isFigureMode && figureCard.activeValueId != null}
                speakerFor={(quote) => resolveQuoteSpeaker({
                  text: quote.text,
                  respondentId: quote.respondentId ?? undefined,
                  respondentLabel: quote.respondentLabel ?? undefined,
                  assetId: quote.assetId ?? undefined,
                }, quoteProfileIndex, quoteRowIndex)}
                initialsFor={speakerInitials}
              />
            )}
          </div>
        ) : isChartQuestion ? (
          <div>
            {renderChart()}
            {segmentQuestion?.type === 'checkbox' && (
              <p className="text-xs text-muted-foreground mt-4">Multi-select question — totals may exceed 100%</p>
            )}
          </div>
        ) : (
          renderChart()
        )}
      </div>
    </div>
  )
}
